import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Trash2, ShoppingCart, Search, Calendar, X, Upload, FileSpreadsheet, FileText, Zap, Package, Pencil, Paperclip, Loader2 } from 'lucide-react';
import { formatCurrency, formatDate, cn } from '../lib/utils';
import { Checkbox } from '../components/ui/checkbox';
import * as XLSX from 'xlsx';
import { useIsAdmin } from '../context/AuthContext';
import SupplierInvoiceImport from './SupplierInvoiceImport';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Quick Add Product Search Component for Purchases
const QuickProductSearch = ({ products, onSelect, disabled, inputRef }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const normalizeSearch = (str) => {
    return (str || '').toLowerCase().replace(/[×x]/g, '').replace(/\s+/g, '').replace(/\./g, '');
  };

  const filteredProducts = searchTerm.length > 0
    ? products.filter(product => {
        const search = normalizeSearch(searchTerm);
        const name = normalizeSearch(product.name);
        const size = normalizeSearch(product.size);
        const brand = normalizeSearch(product.brand);
        const sku = normalizeSearch(product.sku);
        const hsn = normalizeSearch(product.hsn_code);
        
        return name.includes(search) || 
               size.includes(search) ||
               brand.includes(search) ||
               sku.includes(search) ||
               hsn.includes(search) ||
               size.replace(/\./g, '').includes(search);
      }).slice(0, 8)
    : [];

  const handleSelect = useCallback((product) => {
    onSelect(product);
    setSearchTerm('');
    setIsOpen(false);
    setSelectedIndex(0);
  }, [onSelect]);

  const handleKeyDown = (e) => {
    if (!isOpen && e.key === 'ArrowDown' && filteredProducts.length > 0) {
      setIsOpen(true);
      return;
    }

    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, filteredProducts.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredProducts[selectedIndex]) {
          handleSelect(filteredProducts[selectedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchTerm]);

  return (
    <div className="relative">
      <div className="relative">
        <Zap className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-teal-500" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Quick add: Search product name, size..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="pl-10 h-11 bg-teal-50/50 border-teal-200 focus:border-teal-400"
          data-testid="purchase-quick-search"
        />
      </div>

      {isOpen && filteredProducts.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg overflow-hidden">
          <div className="max-h-64 overflow-y-auto">
            {filteredProducts.map((product, index) => (
              <button
                key={product.id}
                onClick={() => handleSelect(product)}
                className={cn(
                  "w-full px-3 py-2 flex items-center justify-between text-left transition-colors",
                  index === selectedIndex ? "bg-teal-50" : "hover:bg-slate-50",
                  index !== filteredProducts.length - 1 && "border-b border-slate-100"
                )}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={cn(
                      "font-medium text-sm",
                      index === selectedIndex ? "text-teal-700" : "text-slate-900"
                    )}>
                      {product.name}
                    </span>
                    {product.size && (
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-slate-100">
                        {product.size}
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {product.brand} • ₹{product.purchase_price} • GST {product.gst_percentage}%
                  </div>
                </div>
              </button>
            ))}
          </div>
          <div className="px-3 py-2 bg-slate-50 border-t border-slate-100 text-[10px] text-slate-400">
            ↑↓ Navigate • Enter Select • Esc Close
          </div>
        </div>
      )}

      {isOpen && searchTerm.length > 0 && filteredProducts.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-lg border border-slate-200 shadow-lg p-4">
          <div className="flex items-center gap-2 text-slate-500 text-sm">
            <Package className="w-4 h-4" />
            No products found for "{searchTerm}"
          </div>
        </div>
      )}
    </div>
  );
};

const Purchases = () => {
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [purchases, setPurchases] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const quickSearchRef = useRef(null);
  const fileInputRef = useRef(null);
  const pdfInputRef = useRef(null);
  const [pdfImporting, setPdfImporting] = useState(false);
  const [pdfProgress, setPdfProgress] = useState({ done: 0, total: 0 });
  const [pdfAttachmentPaths, setPdfAttachmentPaths] = useState([]);
  const [pdfAttachmentNames, setPdfAttachmentNames] = useState([]);
  // Post-save prompt: after a successful purchase save, offer to jump to Sales Invoice
  const [showPostSaveDialog, setShowPostSaveDialog] = useState(false);
  const [postSaveItems, setPostSaveItems] = useState([]);
  const [postSaveSummary, setPostSaveSummary] = useState({ count: 0, invoice: '', vendor: '' });

  // Multi-select state
  const [selectedPurchases, setSelectedPurchases] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Supplier Invoice Import dialog
  const [isSupplierInvoiceOpen, setIsSupplierInvoiceOpen] = useState(false);

  // Edit state
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    vendor_invoice_number: '',
    purchase_date: '',
    batch_number: '',
    quantity: '',
    purchase_price: '',
    gst_percentage: 12
  });

  // Multi-line purchase form
  const [formData, setFormData] = useState({
    vendor_id: '',
    vendor_invoice_number: '',
    purchase_date: new Date().toISOString().split('T')[0],
    items: [{ product_id: '', product_name: '', brand: '', size: '', batch_number: '', quantity: '', purchase_price: '', gst_percentage: 12 }]
  });

  // Excel sheet selection state
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheetNames, setExcelSheetNames] = useState([]);
  const [showSheetSelector, setShowSheetSelector] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [purchasesRes, vendorsRes, productsRes] = await Promise.all([
        axios.get(`${API_URL}/purchases`),
        axios.get(`${API_URL}/vendors`),
        axios.get(`${API_URL}/products`)
      ]);
      setPurchases(purchasesRes.data);
      setVendors(vendorsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { _key: `manual-${Date.now()}`, product_id: '', product_name: '', brand: '', size: '', batch_number: '', quantity: '', purchase_price: '', gst_percentage: 12 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const handleProductSelect = (index, product) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      product_id: product.id,
      product_name: product.name,
      brand: product.brand || '',
      size: product.size || '',
      purchase_price: product.purchase_price || '',
      gst_percentage: product.gst_percentage || 12
    };
    setFormData({ ...formData, items: newItems });

    // Focus on batch number field
    setTimeout(() => {
      const batchInput = document.querySelector(`[data-testid="item-batch-${index}"]`);
      if (batchInput) batchInput.focus();
    }, 100);
  };

  // Quick add from search bar
  const handleQuickAdd = useCallback((product) => {
    setFormData(prev => {
      // Check if first item is empty
      const hasEmptyFirstItem = prev.items.length === 1 && 
        !prev.items[0].product_id && 
        !prev.items[0].batch_number;

      let newItems;
      let targetIndex;

      if (hasEmptyFirstItem) {
        newItems = [{
          product_id: product.id,
          product_name: product.name,
          brand: product.brand || '',
          size: product.size || '',
          batch_number: '',
          quantity: '',
          purchase_price: product.purchase_price || '',
          gst_percentage: product.gst_percentage || 12
        }];
        targetIndex = 0;
      } else {
        newItems = [...prev.items, {
          product_id: product.id,
          product_name: product.name,
          brand: product.brand || '',
          size: product.size || '',
          batch_number: '',
          quantity: '',
          purchase_price: product.purchase_price || '',
          gst_percentage: product.gst_percentage || 12
        }];
        targetIndex = newItems.length - 1;
      }

      // Focus batch field
      setTimeout(() => {
        const batchInput = document.querySelector(`[data-testid="item-batch-${targetIndex}"]`);
        if (batchInput) batchInput.focus();
      }, 100);

      return { ...prev, items: newItems };
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all items have required fields
    const validItems = formData.items.filter(item => 
      item.product_id && item.batch_number && item.quantity && item.purchase_price
    );

    if (validItems.length === 0) {
      toast.error('Please add at least one complete item');
      return;
    }

    // Block unmatched PDF-imported items — user must pick a product first
    const unmatched = formData.items.filter(item =>
      !item.product_id && (item.batch_number || item.quantity || item.purchase_price || item.extracted)
    );
    if (unmatched.length > 0) {
      toast.error(`${unmatched.length} row(s) are not linked to a product yet. Please select a product for each highlighted row.`);
      return;
    }

    // Size is mandatory
    const missingSize = validItems.find(item => {
      const product = products.find(p => p.id === item.product_id);
      const s = item.size || product?.size || '';
      return !s || String(s).trim() === '' || String(s).trim() === '-';
    });
    if (missingSize) {
      toast.error(`Size is mandatory. Please set a size on the product "${missingSize.product_name || ''}" before saving.`);
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        vendor_id: formData.vendor_id,
        vendor_invoice_number: formData.vendor_invoice_number,
        purchase_date: formData.purchase_date,
        attachment_paths: pdfAttachmentPaths.length > 0 ? pdfAttachmentPaths : null,
        items: validItems.map(item => {
          // Get product details for size/brand
          const product = products.find(p => p.id === item.product_id);
          return {
            product_id: item.product_id,
            product_name: item.product_name || product?.name || '',
            brand: item.brand || product?.brand || '',
            size: item.size || product?.size || '',
            batch_number: item.batch_number,
            quantity: parseInt(item.quantity),
            purchase_price: parseFloat(item.purchase_price),
            gst_percentage: parseFloat(item.gst_percentage)
          };
        })
      };

      const res = await axios.post(`${API_URL}/purchases/bulk`, payload);
      toast.success(`${validItems.length} purchase entries recorded successfully`);

      // Build "invoice items" for Sales pre-fill from what we just saved.
      // Prefer the freshly-updated selling_price returned by the backend
      // (respects manual-override flag). Fall back to the product's stored
      // selling_price if the backend didn't recompute it (manual case).
      const priceMap = {};
      (res.data?.product_price_updates || []).forEach(u => {
        priceMap[u.product_id] = u; // { new_selling_price, selling_price_manually_edited, ... }
      });
      const invoiceItems = validItems.map(item => {
        const product = products.find(p => p.id === item.product_id);
        const upd = priceMap[item.product_id];
        // upd.new_selling_price is null when the user has manually overridden the product's selling price
        const computedSelling = upd && upd.new_selling_price != null ? upd.new_selling_price : null;
        const fallbackSelling = product?.selling_price || '';
        return {
          product_id: item.product_id,
          batch_number: item.batch_number,
          quantity: item.quantity.toString(),
          selling_price: (computedSelling != null ? computedSelling : fallbackSelling) || '',
          gst_percentage: parseFloat(item.gst_percentage) || 12,
        };
      });

      // Cache items for the "Generate Sales Invoice" button in the post-save dialog
      setPostSaveItems(invoiceItems);
      const vendorName = vendors.find(v => v.id === formData.vendor_id)?.name || '';
      setPostSaveSummary({ count: validItems.length, invoice: formData.vendor_invoice_number, vendor: vendorName });

      setIsDialogOpen(false);
      resetForm();
      fetchData();
      // Open post-save prompt AFTER purchase dialog closes for clean transition
      setShowPostSaveDialog(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save purchases');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Navigate to Sales module and pre-fill items using the existing invoice flow
  const handleGenerateSalesInvoice = () => {
    setShowPostSaveDialog(false);
    if (postSaveItems.length === 0) return;
    navigate('/sales?create=true', { state: { inventoryItems: postSaveItems } });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this purchase? This will also update inventory.')) return;
    try {
      await axios.delete(`${API_URL}/purchases/${id}`);
      toast.success('Purchase deleted successfully');
      setSelectedPurchases(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete purchase');
    }
  };

  // Multi-select handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedPurchases(new Set(filteredPurchases.map(p => p.id)));
    } else {
      setSelectedPurchases(new Set());
    }
  };

  const handleSelectPurchase = (purchaseId, checked) => {
    setSelectedPurchases(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(purchaseId);
      } else {
        newSet.delete(purchaseId);
      }
      return newSet;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedPurchases.size === 0) return;
    
    const count = selectedPurchases.size;
    if (!window.confirm(`Are you sure you want to delete ${count} purchase(s)? This will also update inventory and cannot be undone.`)) return;
    
    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;
    
    for (const purchaseId of selectedPurchases) {
      try {
        await axios.delete(`${API_URL}/purchases/${purchaseId}`);
        successCount++;
      } catch (error) {
        errorCount++;
      }
    }
    
    setIsDeleting(false);
    setSelectedPurchases(new Set());
    
    if (errorCount === 0) {
      toast.success(`Successfully deleted ${successCount} purchase(s)`);
    } else {
      toast.warning(`Deleted ${successCount} purchase(s), ${errorCount} failed`);
    }
    
    fetchData();
  };

  // Edit handlers
  const openEditDialog = (purchase) => {
    setEditingPurchase(purchase);
    setEditFormData({
      vendor_invoice_number: purchase.vendor_invoice_number,
      purchase_date: purchase.purchase_date,
      batch_number: purchase.batch_number,
      quantity: purchase.quantity.toString(),
      purchase_price: purchase.purchase_price.toString(),
      gst_percentage: purchase.gst_percentage || 12
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingPurchase) return;

    setIsSubmitting(true);
    try {
      const payload = {
        vendor_invoice_number: editFormData.vendor_invoice_number,
        purchase_date: editFormData.purchase_date,
        batch_number: editFormData.batch_number,
        quantity: parseInt(editFormData.quantity),
        purchase_price: parseFloat(editFormData.purchase_price),
        gst_percentage: parseFloat(editFormData.gst_percentage)
      };

      await axios.put(`${API_URL}/purchases/${editingPurchase.id}`, payload);
      toast.success('Purchase updated successfully');
      setIsEditDialogOpen(false);
      setEditingPurchase(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update purchase');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open a stored purchase PDF attachment. Uses axios so auth headers are sent,
  // then creates a blob URL and opens it in a new tab (no downloads).
  const openAttachment = async (attachmentRef) => {
    if (!attachmentRef) return;
    // attachmentRef looks like 'purchase_pdfs/{uuid}.pdf' — pull the filename
    const filename = attachmentRef.split('/').pop();
    if (!filename) return;
    try {
      const res = await axios.get(`${API_URL}/purchases/attachment/${filename}`, {
        responseType: 'blob',
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      // Cleanup the blob URL after a delay so the new tab has time to load it
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      if (!win) toast.info('Popup blocked. Please allow popups for this site.');
    } catch (err) {
      toast.error('Failed to open attachment');
    }
  };

  const resetForm = () => {
    setFormData({
      vendor_id: '',
      vendor_invoice_number: '',
      purchase_date: new Date().toISOString().split('T')[0],
      items: [{ product_id: '', product_name: '', brand: '', size: '', batch_number: '', quantity: '', purchase_price: '', gst_percentage: 12 }]
    });
    setPdfAttachmentPaths([]);
    setPdfAttachmentNames([]);
    setPdfProgress({ done: 0, total: 0 });
  };

  // Merge two item arrays: same (product_id||fallback-key) + batch_number => sum quantity.
  // Fallback key for unmatched items: description|brand|size (case-insensitive, whitespace-stripped)
  const _mergeItems = (existing, incoming) => {
    const keyOf = (it) => {
      const batch = (it.batch_number || '').trim().toLowerCase();
      if (it.product_id) return `p:${it.product_id}|b:${batch}`;
      const norm = (s) => (s || '').trim().toLowerCase().replace(/\s+/g, '');
      const desc = norm(it.extracted?.product_description || it.product_name);
      const br = norm(it.extracted?.brand || it.brand);
      const sz = norm(it.extracted?.size || it.size);
      return `u:${desc}|${br}|${sz}|b:${batch}`;
    };

    const map = new Map();
    const order = [];
    const push = (it) => {
      const k = keyOf(it);
      if (map.has(k)) {
        const prev = map.get(k);
        const prevQty = parseInt(prev.quantity) || 0;
        const addQty = parseInt(it.quantity) || 0;
        prev.quantity = String(prevQty + addQty);
        // Keep prev's price/gst — user can edit; if prev had no price and new does, take new
        if ((!prev.purchase_price || parseFloat(prev.purchase_price) === 0) && it.purchase_price) {
          prev.purchase_price = String(it.purchase_price);
        }
      } else {
        map.set(k, { ...it });
        order.push(k);
      }
    };
    (existing || []).forEach(push);
    (incoming || []).forEach(push);
    return order.map((k) => map.get(k));
  };

  // Multi-PDF Import — reads N vendor invoice PDFs, merges into a single form
  const handlePdfImport = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Validate all are PDFs first
    const nonPdf = files.find(f => !f.name.toLowerCase().endsWith('.pdf'));
    if (nonPdf) {
      toast.error(`"${nonPdf.name}" is not a PDF. Please select PDF files only.`);
      e.target.value = '';
      return;
    }

    setPdfImporting(true);
    setPdfProgress({ done: 0, total: files.length });

    const parsedResults = [];
    const errors = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API_URL}/purchases/parse-pdf`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        parsedResults.push({ file, data: res.data || {} });
      } catch (err) {
        const msg = err.response?.data?.detail || 'Failed to parse';
        errors.push(`${file.name}: ${msg}`);
      } finally {
        setPdfProgress({ done: i + 1, total: files.length });
      }
    }

    setPdfImporting(false);
    e.target.value = '';

    if (parsedResults.length === 0) {
      toast.error(`No PDFs could be parsed. ${errors.slice(0, 2).join(' | ')}`, { duration: 8000 });
      return;
    }

    // Assemble a merged form update off of current formData
    const first = parsedResults[0].data;

    // Vendor: prefer existing vendor selection; else first parsed's matched vendor
    let nextVendorId = formData.vendor_id;
    if (!nextVendorId) {
      const firstVendorMatched = parsedResults.find(r => r.data.vendor?.id);
      nextVendorId = firstVendorMatched?.data.vendor?.id || '';
    }

    // Invoice numbers: keep existing then append new distinct ones with " + "
    const existingInvoice = (formData.vendor_invoice_number || '').trim();
    const invoicePieces = existingInvoice ? [existingInvoice] : [];
    parsedResults.forEach(r => {
      const n = (r.data.vendor_invoice_number || '').trim();
      if (n && !invoicePieces.includes(n)) invoicePieces.push(n);
    });
    const nextInvoice = invoicePieces.join(' + ');

    // Date: latest across parsed PDFs; fall back to existing form date only if no PDF has one
    const pdfDates = parsedResults
      .map(r => r.data.purchase_date)
      .filter(Boolean)
      .sort();
    const nextDate = pdfDates.pop() || formData.purchase_date;

    // Items: flatten all parsed items, tag each with matched flag; then merge with existing rows
    const allIncoming = [];
    parsedResults.forEach(r => {
      (r.data.items || []).forEach(it => {
        allIncoming.push({
          _key: `pdf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          product_id: it.product_id || '',
          product_name: it.product_name || '',
          brand: it.brand || '',
          size: it.size || '',
          batch_number: it.batch_number || '',
          quantity: it.quantity ? String(it.quantity) : '',
          purchase_price: it.purchase_price ? String(it.purchase_price) : '',
          gst_percentage: it.gst_percentage || 12,
          matched: !!it.matched,
          extracted: it.extracted || null,
        });
      });
    });

    // Drop the pristine blank row from existing items when merging
    const existingItemsClean = (formData.items || []).filter(item =>
      item.product_id || item.batch_number || item.quantity || item.purchase_price || item.extracted
    );
    const mergedItems = _mergeItems(existingItemsClean, allIncoming);
    const finalItems = mergedItems.length > 0
      ? mergedItems
      : [{ _key: `manual-${Date.now()}`, product_id: '', product_name: '', brand: '', size: '', batch_number: '', quantity: '', purchase_price: '', gst_percentage: 12 }];

    setFormData(prev => ({
      ...prev,
      vendor_id: nextVendorId || prev.vendor_id,
      vendor_invoice_number: nextInvoice || prev.vendor_invoice_number,
      purchase_date: nextDate || prev.purchase_date,
      items: finalItems,
    }));

    // Attachments: append to existing list
    setPdfAttachmentPaths(prev => [
      ...prev,
      ...parsedResults.map(r => r.data.attachment_path).filter(Boolean),
    ]);
    setPdfAttachmentNames(prev => [
      ...prev,
      ...parsedResults.map(r => r.file.name),
    ]);

    // Summarise
    const totalIncoming = allIncoming.length;
    const totalMerged = totalIncoming - (mergedItems.length - existingItemsClean.length);
    const distinctVendors = new Set(parsedResults.map(r => (r.data.vendor?.name || '').trim().toLowerCase()).filter(Boolean));
    const unmatchedTotal = mergedItems.filter(x => !x.product_id).length;

    const msgParts = [];
    msgParts.push(`Imported ${parsedResults.length} PDF${parsedResults.length > 1 ? 's' : ''}: ${totalIncoming} line item(s)`);
    if (totalMerged > 0) msgParts.push(`${totalMerged} duplicate SKU/batch line(s) merged (quantities combined).`);
    if (distinctVendors.size > 1) msgParts.push('Note: PDFs are from multiple vendors — please pick the correct vendor.');
    if (unmatchedTotal > 0) msgParts.push(`${unmatchedTotal} row(s) need manual product selection (highlighted).`);
    if (errors.length > 0) msgParts.push(`${errors.length} PDF(s) failed to parse.`);
    toast.success(msgParts.join(' '), { duration: 9000 });

    if (errors.length > 0) {
      toast.error(errors.slice(0, 3).join(' | '), { duration: 8000 });
    }
  };

  // Excel Import - with auto-create for missing products
  const handleExcelImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetNames = workbook.SheetNames;

        if (sheetNames.length > 1) {
          // Multiple sheets — let user choose
          setExcelWorkbook(workbook);
          setExcelSheetNames(sheetNames);
          setShowSheetSelector(true);
        } else {
          // Single sheet — process immediately
          await processExcelSheet(workbook, sheetNames[0]);
        }
      } catch (error) {
        console.error('Excel parse error:', error);
        toast.error('Failed to parse Excel file');
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const handleSheetSelect = async (sheetName) => {
    setShowSheetSelector(false);
    if (excelWorkbook) {
      await processExcelSheet(excelWorkbook, sheetName);
      setExcelWorkbook(null);
      setExcelSheetNames([]);
    }
  };

  const processExcelSheet = async (workbook, sheetName) => {
    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet);

    if (rows.length === 0) {
      toast.error(`No data found in sheet "${sheetName}"`);
      return;
    }

    // Work with a mutable copy of products for auto-creation
    let currentProducts = [...products];
    const importedItems = [];
    const errors = [];
    let autoGeneratedBatches = 0;
    let autoCreatedProducts = 0;

    // Normalize helper
    const norm = (str) => (str || '').trim().toLowerCase();

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      const rawProductName = (row['Product Name'] || row['product_name'] || row['Product'] || row['product'] || '').toString().trim();
      const brand = (row['Brand'] || row['brand'] || '').toString().trim();
      const size = (row['Size'] || row['size'] || '').toString().trim();
      const batchNumber = (row['Batch Number'] || row['batch_number'] || row['Batch'] || row['batch'] || '').toString().trim();
      const quantity = parseInt(row['Quantity'] || row['quantity'] || row['Qty'] || row['qty'] || 1);
      const purchasePrice = parseFloat(row['Purchase Price'] || row['purchase_price'] || row['Price'] || row['price'] || 0);
      const gstPercentage = parseFloat(row['GST %'] || row['GST'] || row['gst'] || row['gst_percentage'] || 12);

      // Clean product name: strip brand suffix if present
      let productName = rawProductName;
      if (brand && productName.toLowerCase().endsWith(brand.toLowerCase())) {
        productName = productName.slice(0, -brand.length).trim();
      }

      // Find matching product
      let product = null;

      // 1. Exact match: name + brand + size
      if (productName && brand && size) {
        product = currentProducts.find(p =>
          norm(p.name) === norm(productName) &&
          norm(p.brand) === norm(brand) &&
          norm(p.size) === norm(size)
        );
      }

      // 2. Partial name + brand + size
      if (!product && productName && brand && size) {
        product = currentProducts.find(p =>
          norm(p.name).includes(norm(productName)) &&
          norm(p.brand) === norm(brand) &&
          norm(p.size) === norm(size)
        );
      }

      // 3. Brand + size
      if (!product && brand && size) {
        product = currentProducts.find(p =>
          norm(p.brand) === norm(brand) &&
          norm(p.size) === norm(size)
        );
      }

      // 4. Name + size
      if (!product && productName && size) {
        product = currentProducts.find(p =>
          norm(p.name).includes(norm(productName)) &&
          norm(p.size) === norm(size)
        );
      }

      // 5. Name + brand (no size)
      if (!product && productName && brand && !size) {
        product = currentProducts.find(p =>
          norm(p.name).includes(norm(productName)) &&
          norm(p.brand) === norm(brand)
        );
      }

      // --- If still not found, AUTO-CREATE the product ---
      if (!product) {
        try {
          const displayName = productName || rawProductName;
          const newProduct = await axios.post(`${API_URL}/products`, {
            name: displayName,
            brand: brand || 'Unknown',
            size: size || '-',
            hsn_code: '90211000',
            gst_percentage: gstPercentage || 12,
            purchase_price: purchasePrice || 0,
            selling_price: Math.round(purchasePrice * 1.15) || 0
          });
          product = newProduct.data;
          currentProducts.push(product);
          autoCreatedProducts++;
        } catch (createErr) {
          errors.push({
            row: rowIndex + 2,
            message: `Could not create product: "${rawProductName}" ${brand ? `[${brand}]` : ''} ${size ? `(${size})` : ''}`
          });
          continue;
        }
      }

      const finalBatchNumber = batchNumber || `BATCH-${Date.now()}-${rowIndex + 1}`;
      if (!batchNumber) autoGeneratedBatches++;

      if (!quantity || quantity <= 0) {
        errors.push({
          row: rowIndex + 2,
          message: `Invalid quantity for: ${product.name} ${product.size || ''}`
        });
        continue;
      }

      importedItems.push({
        _key: `import-${Date.now()}-${rowIndex}`,
        product_id: product.id,
        product_name: product.name,
        brand: product.brand || brand || '',
        size: product.size || size || '',
        batch_number: finalBatchNumber,
        quantity: quantity.toString(),
        purchase_price: purchasePrice || product.purchase_price || '',
        gst_percentage: gstPercentage || product.gst_percentage || 12
      });
    }

    // REPLACE items (not append)
    if (importedItems.length > 0) {
      // Refresh products list FIRST (includes any auto-created products)
      if (autoCreatedProducts > 0) {
        try {
          const res = await axios.get(`${API_URL}/products`);
          setProducts(res.data);
        } catch (refetchErr) {
          // Non-fatal — the form already has the imported items; user can still save.
          // Products list will refresh on next fetchData() call.
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to refresh products list after Excel import:', refetchErr);
          }
        }
      }

      setFormData(prev => ({
        ...prev,
        items: importedItems
      }));

      let successMsg = `Imported ${importedItems.length} item(s) from "${sheetName}"`;
      if (autoCreatedProducts > 0) {
        successMsg += ` (${autoCreatedProducts} new product(s) added to catalog)`;
      }
      if (autoGeneratedBatches > 0) {
        successMsg += `. ${autoGeneratedBatches} batch number(s) auto-generated.`;
      }
      toast.success(successMsg, { duration: 6000 });
    }

    if (errors.length > 0) {
      const errorMsg = errors.slice(0, 3).map(e => `Row ${e.row}: ${e.message}`).join(' | ');
      const moreErrors = errors.length > 3 ? ` ...and ${errors.length - 3} more errors` : '';
      toast.error(`Import issues: ${errorMsg}${moreErrors}`, { duration: 8000 });
    }

    if (importedItems.length === 0 && errors.length > 0) {
      const hasHsnCode = rows[0] && ('HSN Code' in rows[0] || 'hsn_code' in rows[0]);
      const hasSellingPrice = rows[0] && ('Selling Price' in rows[0] || 'selling_price' in rows[0]);
      const hasBatchNumber = rows[0] && ('Batch Number' in rows[0] || 'batch_number' in rows[0] || 'Batch' in rows[0]);
      if ((hasHsnCode || hasSellingPrice) && !hasBatchNumber) {
        toast.warning('This looks like a Product file. Purchase import expects: Product Name, Brand, Size, Batch Number, Quantity, Purchase Price, GST %', { duration: 10000 });
      }
    }
  };

  const downloadTemplate = () => {
    const template = [
      { 
        'Product Name': 'Dental Implant', 
        'Brand': 'Adin',
        'Size': '3.75 x 10', 
        'Batch Number': 'BATCH-001', 
        'Quantity': 10, 
        'Purchase Price': 3000,
        'GST %': 12
      },
      { 
        'Product Name': 'Dental Implant', 
        'Brand': 'Adin',
        'Size': '4.2 x 11.5', 
        'Batch Number': 'BATCH-002', 
        'Quantity': 5, 
        'Purchase Price': 3500,
        'GST %': 12
      }
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 18 }, // Product Name
      { wch: 12 }, // Brand
      { wch: 12 }, // Size
      { wch: 15 }, // Batch Number
      { wch: 10 }, // Quantity
      { wch: 15 }, // Purchase Price
      { wch: 8 }   // GST %
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Purchase Items');
    XLSX.writeFile(wb, 'purchase_import_template.xlsx');
    toast.success('Template downloaded');
  };

  const filteredPurchases = purchases.filter(p => {
    const search = searchTerm.toLowerCase();
    // Find product to get size/dimension and HSN
    const product = products.find(pr => pr.id === p.product_id);
    const size = product?.size?.toLowerCase() || '';
    const hsn = product?.hsn_code?.toLowerCase() || '';
    const brand = product?.brand?.toLowerCase() || '';
    
    return p.vendor_name.toLowerCase().includes(search) ||
      p.product_name.toLowerCase().includes(search) ||
      p.vendor_invoice_number.toLowerCase().includes(search) ||
      p.batch_number.toLowerCase().includes(search) ||
      size.includes(search) ||
      hsn.includes(search) ||
      brand.includes(search);
  });

  // Check if all filtered purchases are selected
  const allSelected = filteredPurchases.length > 0 && filteredPurchases.every(p => selectedPurchases.has(p.id));
  const someSelected = selectedPurchases.size > 0;

  // Calculate totals for form
  const calculateTotals = () => {
    let taxable = 0;
    let gst = 0;
    formData.items.forEach(item => {
      if (item.quantity && item.purchase_price) {
        const itemTaxable = parseFloat(item.quantity) * parseFloat(item.purchase_price);
        taxable += itemTaxable;
        gst += itemTaxable * (parseFloat(item.gst_percentage || 12) / 100);
      }
    });
    return { taxable, gst, total: taxable + gst };
  };

  const totals = calculateTotals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Purchases</h1>
          <p className="text-slate-500 text-sm mt-1">Record purchase invoices from vendors</p>
        </div>
        {isAdmin && (
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => setIsSupplierInvoiceOpen(true)}
            data-testid="import-supplier-invoice-btn"
            disabled={vendors.length === 0 || products.length === 0}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Import Supplier Invoice
          </Button>
          <Button
            onClick={() => { resetForm(); setIsDialogOpen(true); }}
            className="bg-teal-600 hover:bg-teal-700"
            data-testid="add-purchase-btn"
            disabled={vendors.length === 0 || products.length === 0}
          >
            <Plus className="w-4 h-4 mr-2" />
            Record Purchase
          </Button>
        </div>
        )}
      </div>

      {/* Warning if no vendors or products */}
      {(vendors.length === 0 || products.length === 0) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <p className="text-sm text-amber-800">
              {vendors.length === 0 && 'Please add vendors first. '}
              {products.length === 0 && 'Please add products first.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Search and Bulk Actions */}
      <Card className="border border-slate-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between gap-4">
            <div className="relative max-w-md flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by batch, brand, size, HSN..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="purchase-search-input"
              />
            </div>
            {someSelected && (
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">
                  {selectedPurchases.size} selected
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={isDeleting}
                  data-testid="bulk-delete-purchases-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {isDeleting ? 'Deleting...' : 'Delete Selected'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedPurchases(new Set())}
                  data-testid="clear-purchase-selection-btn"
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-slate-200">
        <CardContent className="p-0">
          {filteredPurchases.length === 0 ? (
            <div className="empty-state py-12">
              <ShoppingCart className="empty-state-icon" />
              <p className="text-slate-500">No purchases found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-purchases-checkbox"
                        aria-label="Select all purchases"
                      />
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Entry #</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Date</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Vendor</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Vendor Inv #</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Product</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Size</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase">Batch</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Qty</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Taxable</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">GST</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Total</TableHead>
                    <TableHead className="text-xs font-semibold text-slate-500 uppercase text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPurchases.map((purchase) => {
                    const product = products.find(p => p.id === purchase.product_id);
                    return (
                    <TableRow 
                      key={purchase.id} 
                      data-testid={`purchase-row-${purchase.id}`}
                      className={selectedPurchases.has(purchase.id) ? 'bg-teal-50' : ''}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedPurchases.has(purchase.id)}
                          onCheckedChange={(checked) => handleSelectPurchase(purchase.id, checked)}
                          data-testid={`select-purchase-${purchase.id}`}
                          aria-label={`Select purchase ${purchase.vendor_invoice_number}`}
                        />
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-xs text-teal-700 bg-teal-50 px-1.5 py-0.5 rounded">
                          {purchase.purchase_number || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-slate-600">
                          <Calendar className="w-3 h-3" />
                          {formatDate(purchase.purchase_date)}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium text-slate-900">{purchase.vendor_name}</TableCell>
                      <TableCell className="text-slate-600">{purchase.vendor_invoice_number}</TableCell>
                      <TableCell>
                        <div className="text-slate-900">{purchase.product_name}</div>
                        {product?.brand && (
                          <div className="text-xs text-slate-500">{product.brand}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {product?.size && (
                          <Badge variant="outline" className="text-xs bg-slate-50">
                            {product.size}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="tabular-nums text-slate-600">{purchase.batch_number}</TableCell>
                      <TableCell className="text-right tabular-nums text-slate-900">{purchase.quantity}</TableCell>
                      <TableCell className="text-right tabular-nums text-slate-600">
                        {formatCurrency(purchase.taxable_amount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-slate-600">
                        {formatCurrency(purchase.gst_amount)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums font-medium text-slate-900">
                        {formatCurrency(purchase.total_amount)}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {(() => {
                            const atts = (purchase.attachment_paths && purchase.attachment_paths.length > 0)
                              ? purchase.attachment_paths
                              : (purchase.attachment_path ? [purchase.attachment_path] : []);
                            if (atts.length === 0) return null;
                            if (atts.length === 1) {
                              return (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openAttachment(atts[0])}
                                  title="Open source PDF invoice"
                                  data-testid={`attachment-open-${purchase.id}`}
                                >
                                  <Paperclip className="w-4 h-4 text-teal-600" />
                                </Button>
                              );
                            }
                            return (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    title={`${atts.length} PDF invoices attached`}
                                    data-testid={`attachment-menu-${purchase.id}`}
                                  >
                                    <Paperclip className="w-4 h-4 text-teal-600" />
                                    <span className="ml-0.5 text-[10px] font-semibold text-teal-600">{atts.length}</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64">
                                  <DropdownMenuLabel className="text-xs">Source PDF invoices</DropdownMenuLabel>
                                  <DropdownMenuSeparator />
                                  {atts.map((att, idx) => (
                                    <DropdownMenuItem
                                      key={`${purchase.id}-att-${idx}`}
                                      onClick={() => openAttachment(att)}
                                      data-testid={`attachment-item-${purchase.id}-${idx}`}
                                    >
                                      <FileText className="w-4 h-4 mr-2 text-teal-600" />
                                      <span className="truncate text-xs">PDF #{idx + 1}</span>
                                    </DropdownMenuItem>
                                  ))}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            );
                          })()}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(purchase)}
                            data-testid={`edit-purchase-${purchase.id}`}
                          >
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(purchase.id)}
                            data-testid={`delete-purchase-${purchase.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Multi-Line Purchase Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-teal-600" />
              Record Purchase Invoice
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            {/* Invoice Header */}
            <div className="grid grid-cols-3 gap-4 py-4 border-b">
              <div className="space-y-2">
                <Label>Vendor *</Label>
                <Select
                  value={formData.vendor_id}
                  onValueChange={(value) => setFormData({ ...formData, vendor_id: value })}
                  required
                >
                  <SelectTrigger data-testid="purchase-vendor-select">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vendor Invoice # *</Label>
                <Input
                  value={formData.vendor_invoice_number}
                  onChange={(e) => setFormData({ ...formData, vendor_invoice_number: e.target.value })}
                  placeholder="INV-001"
                  required
                  data-testid="purchase-invoice-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Purchase Date *</Label>
                <Input
                  type="date"
                  value={formData.purchase_date}
                  onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  required
                  data-testid="purchase-date-input"
                />
              </div>
            </div>

            {/* Quick Add Search */}
            <div className="py-3 border-b">
              <QuickProductSearch
                products={products}
                onSelect={handleQuickAdd}
                disabled={!formData.vendor_id}
                inputRef={quickSearchRef}
              />
            </div>

            {/* Line Items */}
            <div className="flex-1 overflow-y-auto py-4 space-y-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-slate-600">
                  {formData.items.filter(i => i.product_id).length} item(s)
                </span>
                <div className="flex gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleExcelImport}
                    accept=".xlsx,.xls"
                    className="hidden"
                  />
                  <input
                    type="file"
                    ref={pdfInputRef}
                    onChange={handlePdfImport}
                    accept="application/pdf,.pdf"
                    multiple
                    className="hidden"
                    data-testid="pdf-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={downloadTemplate}
                    data-testid="template-btn"
                  >
                    <FileSpreadsheet className="w-4 h-4 mr-1" />
                    Template
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="import-excel-btn"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    Import Excel
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => pdfInputRef.current?.click()}
                    disabled={pdfImporting}
                    className="border-teal-300 text-teal-700 hover:bg-teal-50 hover:text-teal-800"
                    data-testid="import-pdf-btn"
                    title="Select one or more vendor invoice PDFs — same SKU/batch across files will be merged"
                  >
                    {pdfImporting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                        Reading {pdfProgress.done}/{pdfProgress.total}...
                      </>
                    ) : (
                      <>
                        <FileText className="w-4 h-4 mr-1" />
                        Import PDF
                      </>
                    )}
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={addItem} data-testid="add-row-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Row
                  </Button>
                </div>
              </div>

              {/* PDF Attachment indicator — one or more PDFs */}
              {pdfAttachmentNames.length > 0 && (
                <div
                  className="bg-teal-50 border border-teal-200 rounded-md px-3 py-2 text-sm text-teal-800"
                  data-testid="pdf-attachment-indicator"
                >
                  <div className="flex items-center gap-2 mb-1">
                    <Paperclip className="w-4 h-4" />
                    <span className="font-medium">
                      {pdfAttachmentNames.length} PDF{pdfAttachmentNames.length > 1 ? 's' : ''} will be attached
                    </span>
                    <button
                      type="button"
                      onClick={() => { setPdfAttachmentPaths([]); setPdfAttachmentNames([]); }}
                      className="ml-auto text-teal-700 hover:text-teal-900"
                      aria-label="Remove all PDF attachments"
                      data-testid="remove-pdf-attachment-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <ul className="pl-6 list-disc space-y-0.5">
                    {pdfAttachmentNames.map((name, i) => (
                      <li key={`att-${i}`} className="truncate text-xs text-teal-700" data-testid={`pdf-attachment-name-${i}`}>{name}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Sheet Selector - shown when Excel has multiple sheets */}
              {showSheetSelector && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3" data-testid="sheet-selector">
                  <p className="text-sm font-medium text-blue-800">This Excel file has {excelSheetNames.length} sheets. Which one to import?</p>
                  <div className="flex flex-wrap gap-2">
                    {excelSheetNames.map((name, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                        onClick={() => handleSheetSelect(name)}
                        data-testid={`select-sheet-${idx}`}
                      >
                        <FileSpreadsheet className="w-4 h-4 mr-1" />
                        {name}
                      </Button>
                    ))}
                  </div>
                  <Button variant="ghost" size="sm" className="text-xs text-slate-500" onClick={() => setShowSheetSelector(false)}>Cancel</Button>
                </div>
              )}

              {/* Items Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="text-xs" style={{width: '30%'}}>Product</TableHead>
                      <TableHead className="text-xs" style={{width: '12%'}}>Size</TableHead>
                      <TableHead className="text-xs" style={{width: '12%'}}>Batch Number</TableHead>
                      <TableHead className="text-xs text-center" style={{width: '8%'}}>Qty</TableHead>
                      <TableHead className="text-xs text-center" style={{width: '10%'}}>Price (₹)</TableHead>
                      <TableHead className="text-xs text-center" style={{width: '8%'}}>GST %</TableHead>
                      <TableHead className="text-xs text-right" style={{width: '12%'}}>Total</TableHead>
                      <TableHead style={{width: '5%'}}></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => {
                      const product = products.find(p => p.id === item.product_id);
                      const itemTaxable = (parseFloat(item.quantity) || 0) * (parseFloat(item.purchase_price) || 0);
                      const itemGst = itemTaxable * ((parseFloat(item.gst_percentage) || 12) / 100);
                      const itemTotal = itemTaxable + itemGst;
                      // Get size from item (imported) or product
                      const itemSize = item.size || product?.size || '';
                      // Row is "unmatched" if PDF import brought data but no product was linked
                      const isUnmatched = !item.product_id && (item.batch_number || item.quantity || item.purchase_price || item.extracted);

                      return (
                        <TableRow
                          key={item._key || `row-${index}`}
                          className={isUnmatched ? 'bg-amber-50 hover:bg-amber-100' : ''}
                          data-testid={isUnmatched ? `unmatched-row-${index}` : undefined}
                        >
                          <TableCell className="p-2">
                            {isUnmatched && item.extracted && (
                              <div className="text-[11px] text-amber-800 mb-1 leading-tight">
                                From PDF: <span className="font-medium">{item.extracted.product_description}</span>
                                {item.extracted.brand ? ` / ${item.extracted.brand}` : ''}
                                {item.extracted.size ? ` (${item.extracted.size})` : ''}
                              </div>
                            )}
                            <Select
                              value={item.product_id}
                              onValueChange={(value) => {
                                const p = products.find(pr => pr.id === value);
                                if (p) handleProductSelect(index, p);
                              }}
                            >
                              <SelectTrigger
                                className={cn('h-9 text-sm', isUnmatched && 'border-amber-500 ring-1 ring-amber-500')}
                                data-testid={`item-product-${index}`}
                              >
                                <SelectValue placeholder={isUnmatched ? 'Select product (required)' : 'Select product'} />
                              </SelectTrigger>
                              <SelectContent>
                                {products.map((p) => (
                                  <SelectItem key={p.id} value={p.id}>
                                    {p.name} {p.brand && `- ${p.brand}`}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="p-2">
                            <span className="text-sm text-slate-600">{itemSize || '-'}</span>
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              value={item.batch_number}
                              onChange={(e) => updateItem(index, 'batch_number', e.target.value)}
                              placeholder="BATCH-001"
                              className="h-9 text-sm"
                              data-testid={`item-batch-${index}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  const qtyInput = document.querySelector(`[data-testid="item-qty-${index}"]`);
                                  if (qtyInput) qtyInput.focus();
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                              className="h-9 text-sm text-center w-full"
                              style={{minWidth: '60px'}}
                              data-testid={`item-qty-${index}`}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  // Focus on quick search for next item
                                  if (quickSearchRef.current) quickSearchRef.current.focus();
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.purchase_price}
                              onChange={(e) => updateItem(index, 'purchase_price', e.target.value)}
                              className="h-9 text-sm text-center w-full"
                              style={{minWidth: '80px'}}
                              data-testid={`item-price-${index}`}
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              type="number"
                              value={item.gst_percentage}
                              onChange={(e) => updateItem(index, 'gst_percentage', e.target.value)}
                              className="h-9 text-sm text-center w-full"
                              style={{minWidth: '50px'}}
                              data-testid={`item-gst-${index}`}
                            />
                          </TableCell>
                          <TableCell className="p-2 text-right">
                            <span className="text-sm font-medium tabular-nums whitespace-nowrap">
                              {itemTotal > 0 ? formatCurrency(itemTotal) : '-'}
                            </span>
                          </TableCell>
                          <TableCell className="p-2">
                            {formData.items.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => removeItem(index)}
                                className="h-8 w-8 p-0"
                              >
                                <X className="w-4 h-4 text-red-500" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Totals */}
            {totals.taxable > 0 && (
              <div className="border-t pt-4 bg-slate-50 -mx-6 px-6 pb-4">
                <div className="flex justify-end">
                  <div className="w-64 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Taxable Amount:</span>
                      <span className="tabular-nums font-medium">{formatCurrency(totals.taxable)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">GST:</span>
                      <span className="tabular-nums font-medium">{formatCurrency(totals.gst)}</span>
                    </div>
                    <div className="flex justify-between text-base pt-2 border-t">
                      <span className="font-semibold">Grand Total:</span>
                      <span className="tabular-nums font-bold text-teal-600">{formatCurrency(totals.total)}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <DialogFooter className="border-t pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-teal-600 hover:bg-teal-700" 
                data-testid="save-purchase-btn"
                disabled={
                  isSubmitting || 
                  !formData.vendor_id || 
                  !formData.vendor_invoice_number ||
                  formData.items.filter(i => i.product_id && i.batch_number && i.quantity && i.purchase_price).length === 0
                }
              >
                {isSubmitting ? 'Saving...' : `Save Purchase (${formData.items.filter(i => i.product_id && i.batch_number && i.quantity && i.purchase_price).length} items)`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Purchase Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <Pencil className="w-5 h-5 text-teal-600" />
              Edit Purchase Entry
            </DialogTitle>
          </DialogHeader>
          
          {editingPurchase && (
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Read-only info */}
              <div className="p-3 bg-slate-50 rounded-lg space-y-1">
                <p className="text-sm font-medium text-slate-700">
                  {editingPurchase.product_name}
                </p>
                <p className="text-xs text-slate-500">
                  Vendor: {editingPurchase.vendor_name}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Invoice Number</Label>
                  <Input
                    value={editFormData.vendor_invoice_number}
                    onChange={(e) => setEditFormData({ ...editFormData, vendor_invoice_number: e.target.value })}
                    data-testid="edit-invoice-number"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Purchase Date</Label>
                  <Input
                    type="date"
                    value={editFormData.purchase_date}
                    onChange={(e) => setEditFormData({ ...editFormData, purchase_date: e.target.value })}
                    data-testid="edit-purchase-date"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Batch Number</Label>
                <Input
                  value={editFormData.batch_number}
                  onChange={(e) => setEditFormData({ ...editFormData, batch_number: e.target.value })}
                  data-testid="edit-batch-number"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="1"
                    value={editFormData.quantity}
                    onChange={(e) => setEditFormData({ ...editFormData, quantity: e.target.value })}
                    data-testid="edit-quantity"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Price (₹)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={editFormData.purchase_price}
                    onChange={(e) => setEditFormData({ ...editFormData, purchase_price: e.target.value })}
                    data-testid="edit-price"
                  />
                </div>
                <div className="space-y-2">
                  <Label>GST %</Label>
                  <Input
                    type="number"
                    value={editFormData.gst_percentage}
                    onChange={(e) => setEditFormData({ ...editFormData, gst_percentage: e.target.value })}
                    data-testid="edit-gst"
                  />
                </div>
              </div>

              {/* Calculated totals preview */}
              <div className="p-3 bg-slate-50 rounded-lg">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Taxable:</span>
                  <span className="tabular-nums">
                    {formatCurrency((parseFloat(editFormData.quantity) || 0) * (parseFloat(editFormData.purchase_price) || 0))}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">GST:</span>
                  <span className="tabular-nums">
                    {formatCurrency(
                      (parseFloat(editFormData.quantity) || 0) * 
                      (parseFloat(editFormData.purchase_price) || 0) * 
                      ((parseFloat(editFormData.gst_percentage) || 12) / 100)
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm font-medium pt-2 border-t mt-2">
                  <span className="text-slate-700">Total:</span>
                  <span className="tabular-nums text-teal-600">
                    {formatCurrency(
                      (parseFloat(editFormData.quantity) || 0) * 
                      (parseFloat(editFormData.purchase_price) || 0) * 
                      (1 + (parseFloat(editFormData.gst_percentage) || 12) / 100)
                    )}
                  </span>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  className="bg-teal-600 hover:bg-teal-700"
                  disabled={isSubmitting}
                  data-testid="save-edit-btn"
                >
                  {isSubmitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Post-Save prompt: offer to generate a Sales Invoice from the just-saved purchase */}
      <Dialog open={showPostSaveDialog} onOpenChange={setShowPostSaveDialog}>
        <DialogContent className="max-w-md" data-testid="post-save-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-teal-600" />
              Purchase saved
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2 py-2">
            <p>
              Recorded <span className="font-semibold text-slate-900">{postSaveSummary.count} item(s)</span>
              {postSaveSummary.invoice && (
                <> from invoice <span className="font-mono text-slate-800">{postSaveSummary.invoice}</span></>
              )}
              {postSaveSummary.vendor && (<> ({postSaveSummary.vendor})</>)}.
            </p>
            <p>Do you want to create a Sales Invoice for these items now? Product, brand, size, batch, quantity and selling price will be pre-filled — you can still edit quantities before saving.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowPostSaveDialog(false)}
              data-testid="post-save-close-btn"
            >
              Not now
            </Button>
            <Button
              type="button"
              className="bg-teal-600 hover:bg-teal-700 text-white"
              onClick={handleGenerateSalesInvoice}
              data-testid="generate-sales-invoice-btn"
              disabled={postSaveItems.length === 0}
            >
              <FileText className="w-4 h-4 mr-2" />
              Generate Sales Invoice
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SupplierInvoiceImport
        open={isSupplierInvoiceOpen}
        onOpenChange={setIsSupplierInvoiceOpen}
        vendors={vendors}
        products={products}
        onPosted={fetchData}
      />
    </div>
  );
};

export default Purchases;
